import { ComponentProps, FC, Suspense, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RelayProp, graphql, createFragmentContainer } from 'react-relay';

import { useIntl } from 'dibs-react-intl';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';

import { messages } from '../messages/pdpDealerDetailsMessages';
import { trackSellerDetailsView } from '../tracking/pdpTracking';
import {
    MAKE_OFFER,
    CONTACT_SELLER,
} from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsContants';

// components
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { PdpRespSellerItemTileCarousel } from '../PdpRespSimilarItems/PdpRespSellerItemTileCarousel/PdpRespSellerItemTileCarousel';
import { PdpRespSellerItemsCarousel } from '../PdpRespProductTileCarousels/PdpRespSellerItemsCarousel';
import { PdpSharedDealerDistinction } from '../PdpSharedDealerDistinctions/PdpSharedDealerDistinction';
import { PdpSharedCallDealerCta } from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta';
import { ContactDealerInlineFormLoader } from 'dibs-buyer-layout/exports/ContactDealerInlineFormLoader';
import { PdpSharedSellerTabsVariantLazy } from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsVariantLazy';
import { PdpSharedDealerDetailsBio } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBio';
import { PdpSharedDealerDetailsHeader } from '../PdpSharedDealerDetailsHeader/PdpSharedDealerDetailsHeader';
import { PdpSharedDealerInfo } from '../PdpSharedDealerInfo/PdpSharedDealerInfo';
import { PdpSharedDealerDetailsReviewContainerLazy } from '../PdpSharedDealerDetailsReview/PdpSharedDealerDetailsReviewContainerLazy';

import { PdpRespDealerDetails_viewer$data } from './__generated__/PdpRespDealerDetails_viewer.graphql';
import { PdpRespDealerDetails_item$data } from './__generated__/PdpRespDealerDetails_item.graphql';
import { PdpRespDealerDetails_user$data } from './__generated__/PdpRespDealerDetails_user.graphql';
import { PdpRespDealerDetails_sku$data } from './__generated__/PdpRespDealerDetails_sku.graphql';

// styles
import styles from './PdpRespDealerDetails.scss';

type ReduxState = {
    relayVariables: { variables: { hasRestyledProductTiles: Maybe<boolean> } };
};

type Props = {
    viewer: PdpRespDealerDetails_viewer$data;
    item: PdpRespDealerDetails_item$data;
    user: PdpRespDealerDetails_user$data | null;
    sku: PdpRespDealerDetails_sku$data | null;
    relay: RelayProp;
    isClient: boolean;
    currency: string;
    dataTn?: string;
};

type SellerItemsCarouselProps = {
    hasRestyledProductTiles: Maybe<boolean>;
} & ComponentProps<typeof PdpRespSellerItemsCarousel> &
    ComponentProps<typeof PdpRespSellerItemTileCarousel>;
const SellerItemsCarousel: FC<SellerItemsCarouselProps> = ({ hasRestyledProductTiles, ...props }) =>
    hasRestyledProductTiles ? (
        <PdpRespSellerItemsCarousel {...props} />
    ) : (
        <PdpRespSellerItemTileCarousel {...props} />
    );

const PdpRespDealerDetailsComponent: FC<Props> = ({
    item,
    sku,
    user,
    viewer,
    currency,
    isClient,
    dataTn,
}) => {
    const intl = useIntl();
    const { hasRestyledProductTiles } = useSelector(
        (state: ReduxState) => state.relayVariables.variables
    );
    const sellerDetailsRef = useRef(null);

    const { seller, dealerDetailsDisplayInfo, isSold: isSoldPdp, isUnpurchasable } = item;
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const dealerDetailsReviewFF = viewer?.dealerDetailsReviewFF;
    const aboutUs = liveStorefrontProfile?.aboutUsUnbranded;
    const isNegotiable = dealerDetailsDisplayInfo?.ctas?.showMakeOffer || false;
    const isUnavailablePdp = isSoldPdp || isUnpurchasable;
    const shouldLoadEmbeddedMakeOffer = isClient && isNegotiable;
    const shouldLoadContactForm = isClient && !isNegotiable;

    if (!seller?.isAccessible) {
        return null;
    }

    const header =
        isRewardsTier2 && aboutUs ? (
            <>
                <div className={styles.detailsHeaderBio} data-tn="pdp-dealer-details-header-bio">
                    <div className={styles.leftSection}>
                        <PdpSharedDealerDetailsHeader
                            seller={seller}
                            user={user}
                            isClient={isClient}
                        />
                    </div>
                    <div className={`${styles.detailsBio} ${styles.rightSection}`}>
                        <PdpSharedDealerDetailsBio seller={seller} isClient={isClient} />
                    </div>
                </div>
                <div className={`${styles.detailsStatsContainer} ${styles.leftSection}`}>
                    <div className={styles.detailsSellerStats}>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedDealerDistinction seller={seller} />
                        </div>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedDealerInfo seller={seller} />
                        </div>
                    </div>
                    {dealerDetailsReviewFF && (
                        <PdpSharedDealerDetailsReviewContainerLazy seller={seller} />
                    )}
                </div>
            </>
        ) : (
            <div className={`${styles.detailsContainer} ${styles.leftSection}`}>
                <div className={styles.detailsHeader} data-tn="pdp-dealer-info">
                    <PdpSharedDealerDetailsHeader seller={seller} user={user} isClient={isClient} />
                </div>
                <div className={styles.detailsSellerStats}>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedDealerDistinction seller={seller} />
                    </div>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedDealerInfo seller={seller} />
                    </div>
                </div>
                {dealerDetailsReviewFF && (
                    <PdpSharedDealerDetailsReviewContainerLazy seller={seller} />
                )}
            </div>
        );

    return (
        <>
            <div className={styles.wrapper} ref={sellerDetailsRef} data-tn={dataTn}>
                <VisibilityTracker
                    elementRef={sellerDetailsRef}
                    onVisibilityChange={({ isVisible }) =>
                        trackSellerDetailsView(isNegotiable, isVisible)
                    }
                />
                {header}

                <div
                    className={`${styles.formContainer} ${styles.rightSection}`}
                    data-tn="pdp-contact-dealer-info"
                >
                    {shouldLoadEmbeddedMakeOffer ? (
                        <Suspense fallback={''}>
                            <PdpSharedSellerTabsVariantLazy
                                viewer={viewer}
                                item={item}
                                sku={sku}
                                user={user}
                                buttonProps={{ size: 'medium' }}
                                currency={currency}
                                tabOrder={
                                    user?.isVerifiedTrade
                                        ? [CONTACT_SELLER, MAKE_OFFER]
                                        : [MAKE_OFFER, CONTACT_SELLER]
                                }
                            />
                        </Suspense>
                    ) : (
                        <ContactDealerInlineFormLoader
                            viewer={viewer}
                            load={shouldLoadContactForm}
                            item={item}
                            user={user}
                            responseTimePos="none"
                            inputProps={{
                                hasAnimatedPlaceholder: true,
                                placeholder: intl.formatMessage(messages.namePlaceholder),
                            }}
                            buttonProps={{ className: styles.contactDealerButton }}
                        />
                    )}
                    {shouldLoadContactForm && <PdpSharedCallDealerCta item={item} user={user} />}
                </div>
            </div>
            {!isUnavailablePdp && (
                <div className={styles.carouselContainer}>
                    <SellerItemsCarousel
                        hasRestyledProductTiles={hasRestyledProductTiles}
                        user={user}
                        item={item}
                        viewer={viewer}
                        currency={currency}
                    />
                </div>
            )}
        </>
    );
};

export const PdpRespDealerDetails = createFragmentContainer(PdpRespDealerDetailsComponent, {
    viewer: graphql`
        fragment PdpRespDealerDetails_viewer on Viewer {
            dealerDetailsReviewFF: featureFlag(feature: "dealerDetailsReview")
            ...ContactDealerInlineForm_viewer
            ...PdpSharedSellerTabsVariant_viewer
            ...PdpRespSellerItemTileCarousel_viewer
                @arguments(userCountryCode: $userCountryCode)
                @skip(if: $hasRestyledProductTiles)
            ...PdpRespSellerItemsCarousel_viewer
                @arguments(userCountryCode: $userCountryCode)
                @include(if: $hasRestyledProductTiles)
        }
    `,
    user: graphql`
        fragment PdpRespDealerDetails_user on User {
            isVerifiedTrade
            ...ContactDealerInlineForm_user
            ...PdpSharedSellerTabsVariant_user
            ...PdpSharedCallDealerCta_user
            ...PdpSharedDealerDetailsHeader_user

            ...PdpRespSellerItemTileCarousel_user @skip(if: $hasRestyledProductTiles)
            ...PdpRespSellerItemsCarousel_user @include(if: $hasRestyledProductTiles)
        }
    `,
    item: graphql`
        fragment PdpRespDealerDetails_item on Item {
            ... @include(if: $fetchPdp) {
                ...PdpSharedCallDealerCta_item
                ...ContactDealerInlineForm_item
                ...PdpSharedSellerTabsVariant_item
                dealerDetailsDisplayInfo: displayInfo {
                    ctas {
                        showMakeOffer
                    }
                }
                seller {
                    serviceId
                }
            }
            isSold
            isUnpurchasable
            seller {
                ...PdpSharedDealerDetailsReviewContainerLazy_seller
                ...PdpSharedDealerDetailsBio_seller
                ...PdpSharedDealerDetailsHeader_seller
                ...PdpSharedDealerDistinction_seller
                ...PdpSharedDealerInfo_seller
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                liveStorefrontProfile {
                    aboutUsUnbranded
                }
                isAccessible
            }
            ...PdpRespSellerItemTileCarousel_item @skip(if: $hasRestyledProductTiles)
            ...PdpRespSellerItemsCarousel_item @include(if: $hasRestyledProductTiles)
        }
    `,
    sku: graphql`
        fragment PdpRespDealerDetails_sku on ItemSkuType {
            ...PdpSharedSellerTabsVariant_sku
        }
    `,
});
